import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Card, Row, Col, Button, message, Tag, Avatar, Popconfirm, Space, Select, Spin } from 'antd';
import { LoadingOutlined, PlusOutlined, DeleteOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import axios from '../../../utils/axiosConfig';

const { Title, Paragraph, Text } = Typography;

const Integrations = () => {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState({});
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const MAX_ACCOUNTS = 3;

  const [connections, setConnections] = useState({
    facebook: [],
    instagram: [],
    google: []
  });

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch locations (workspaces)
  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const { data } = await axios.get('/api/vehicle-tracking/workspaces', { headers: authHeader() });
      const workspaces = Array.isArray(data) ? data : (data.items || []);
      setLocations(workspaces);
      if (workspaces.length > 0 && !selectedLocation) {
        setSelectedLocation(workspaces[0].id);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLocationsLoading(false);
    }
  }, [authHeader, selectedLocation]);

  // Load integrations for selected location
  const loadConnections = useCallback(async () => {
    if (!token || !selectedLocation) return;

    const locationParam = `?location_id=${selectedLocation}`;

    // Load Facebook accounts
    try {
      const fbRes = await fetch(`/api/integrations/facebook/accounts${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (fbRes.ok) {
        const fbData = await fbRes.json();
        const accounts = Array.isArray(fbData) ? fbData : (fbData.accounts || []);
        setConnections(prev => ({ ...prev, facebook: accounts.slice(0, MAX_ACCOUNTS) }));
      }
    } catch (e) {
      console.error('Failed to load Facebook accounts:', e);
    }

    // Load Instagram accounts
    try {
      const igRes = await fetch(`/api/integrations/instagram/accounts${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (igRes.ok) {
        const igData = await igRes.json();
        const accounts = Array.isArray(igData) ? igData : (igData.accounts || []);
        setConnections(prev => ({ ...prev, instagram: accounts.slice(0, MAX_ACCOUNTS) }));
      }
    } catch (e) {
      console.error('Failed to load Instagram accounts:', e);
    }

    // Load Google Ads accounts
    try {
      const googleRes = await fetch(`/api/integrations/google-ads/accounts${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (googleRes.ok) {
        const googleData = await googleRes.json();
        const accounts = Array.isArray(googleData) ? googleData : (googleData.accounts || []);
        setConnections(prev => ({ ...prev, google: accounts.slice(0, MAX_ACCOUNTS) }));
      }
    } catch (e) {
      console.error('Failed to load Google Ads accounts:', e);
    }
  }, [token, selectedLocation]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (selectedLocation) {
      // Reset connections when location changes
      setConnections({ facebook: [], instagram: [], google: [] });
      loadConnections();
    }
  }, [selectedLocation, loadConnections]);

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected) {
      message.success(`${connected} connected successfully!`);
      window.history.replaceState({}, '', '/agency/integrations');
      setTimeout(() => loadConnections(), 500);
    } else if (error) {
      message.error('Failed to connect. Please try again.');
      window.history.replaceState({}, '', '/agency/integrations');
    }
  }, [loadConnections]);

  const getIntegrationName = (id) => ({ facebook: 'Facebook', instagram: 'Instagram', google: 'Google Ads' }[id] || id);

  const handleConnect = (integrationId) => {
    setLoading(prev => ({ ...prev, [`${integrationId}_connect`]: true }));

    const backendUrl = (() => {
      const explicit = process.env.REACT_APP_API_URL;
      if (explicit) return explicit.replace(/\/api$/, '');
      const hostname = window.location.hostname;
      if (hostname === 'app.pathmarkr.com' || hostname === 'pathmarkr.com' || hostname.includes('amazonaws.com'))
        return 'https://api.pathmarkr.com';
      if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:5000';
      return 'https://api.pathmarkr.com';
    })();

    const stateData = JSON.stringify({ userId: currentUser?.id, locationId: selectedLocation, timestamp: Date.now() });

    if (integrationId === 'facebook') {
      const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/meta/callback`);
      // Only request Advanced-Access-approved permissions so real clients can connect.
      // business_management and pages_manage_ads removed (unused + were blocking connects).
      const scope = encodeURIComponent('ads_management,ads_read,pages_read_engagement');
      window.location.href = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${encodeURIComponent(stateData)}&response_type=code`;
    } else if (integrationId === 'instagram') {
      const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/instagram/callback`);
      const scope = encodeURIComponent('instagram_basic,instagram_manage_insights,pages_show_list,pages_read_engagement');
      window.location.href = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${redirectUri}&scope=${scope}&state=${encodeURIComponent(stateData)}&response_type=code`;
    } else if (integrationId === 'google') {
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
      const redirectUri = encodeURIComponent(`${backendUrl}/api/integrations/google-ads/callback`);
      const scope = encodeURIComponent('openid email profile');
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${encodeURIComponent(stateData)}&response_type=code&access_type=offline&prompt=consent`;
    }
  };

  const handleDisconnect = async (integrationId, accountId) => {
    setLoading(prev => ({ ...prev, [`${integrationId}_${accountId}`]: true }));
    try {
      const endpoints = {
        facebook: '/api/integrations/facebook/disconnect',
        instagram: '/api/integrations/instagram/disconnect',
        google: '/api/integrations/google-ads/disconnect'
      };
      const res = await fetch(endpoints[integrationId], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ accountId, locationId: selectedLocation })
      });
      if (!res.ok) throw new Error('Failed to disconnect');
      setConnections(prev => ({
        ...prev,
        [integrationId]: prev[integrationId].filter(acc => acc.id !== accountId)
      }));
      message.success(`${getIntegrationName(integrationId)} account disconnected`);
    } catch (error) {
      message.error(`Failed to disconnect ${getIntegrationName(integrationId)}`);
    } finally {
      setLoading(prev => ({ ...prev, [`${integrationId}_${accountId}`]: false }));
    }
  };

  const integrationsList = [
    {
      id: 'facebook',
      name: 'Facebook Pages',
      description: 'Connect Facebook Pages to create and manage ads from PathMarkr.',
      icon: (
        <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#1877f2" d="M24,4C12.954,4,4,12.954,4,24s8.954,20,20,20s20-8.954,20-20S35.046,4,24,4z"/>
          <path fill="#fff" d="M26.707,29.301h5.176l0.813-5.258h-5.989v-2.874c0-2.184,0.714-4.121,2.757-4.121h3.283V12.46 c-0.577-0.078-1.797-0.248-4.102-0.248c-4.814,0-7.636,2.542-7.636,8.334v3.498H16.06v5.258h4.948v14.452 C21.988,43.9,22.981,44,24,44c0.921,0,1.82-0.084,2.707-0.204V29.301z"/>
        </svg>
      ),
      color: '#1877f2'
    },
    {
      id: 'instagram',
      name: 'Instagram Accounts',
      description: 'Connect Instagram Business accounts to run Instagram ads.',
      icon: <img src="/ig.jpg" alt="Instagram" style={{ width: 36, height: 36, borderRadius: '6px' }} />,
      color: '#E4405F'
    },
    {
      id: 'google',
      name: 'Google Ads',
      description: 'Integrate with Google Ads to manage search and display campaigns.',
      icon: (
        <svg width="36" height="36" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
          <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
          <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
          <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
        </svg>
      ),
      color: '#4285f4'
    }
  ];

  const renderAccountSlot = (integration, slotIndex) => {
    const accounts = connections[integration.id] || [];
    const account = accounts[slotIndex];
    const isConnected = !!account;
    const isLoading = loading[`${integration.id}_connect`];

    if (isConnected) {
      return (
        <div key={`${integration.id}-slot-${slotIndex}`} style={{ padding: '16px', background: '#f6ffed', border: '2px solid #b7eb8f', borderRadius: '8px', marginBottom: '12px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <Avatar size={40} src={account.profile_picture_url} icon={<UserOutlined />} style={{ background: integration.color }} />
                <div>
                  <div><Text strong style={{ fontSize: '14px' }}>{account.name || account.username || 'Account'}</Text></div>
                  <div><Text type="secondary" style={{ fontSize: '12px' }}>{account.email || account.id}</Text></div>
                </div>
              </Space>
              <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
            </div>
            <Popconfirm title="Disconnect this account?" description="You can reconnect later." onConfirm={() => handleDisconnect(integration.id, account.id)} okText="Yes" cancelText="No">
              <Button danger size="small" icon={<DeleteOutlined />} loading={loading[`${integration.id}_${account.id}`]} block>Disconnect</Button>
            </Popconfirm>
          </Space>
        </div>
      );
    }

    return (
      <div key={`${integration.id}-slot-${slotIndex}`} style={{ padding: '16px', background: '#fafafa', border: '2px dashed #d9d9d9', borderRadius: '8px', marginBottom: '12px', textAlign: 'center' }}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <Avatar size={40} icon={<UserOutlined />} style={{ background: '#d9d9d9' }} />
              <div style={{ textAlign: 'left' }}>
                <div><Text type="secondary" style={{ fontSize: '14px' }}>Account Slot {slotIndex + 1}</Text></div>
                <div><Text type="secondary" style={{ fontSize: '12px' }}>Not connected</Text></div>
              </div>
            </Space>
            <Tag icon={<CloseCircleOutlined />} color="default">Empty</Tag>
          </div>
          <Button type="primary" size="small" icon={isLoading ? <LoadingOutlined /> : <PlusOutlined />} onClick={() => handleConnect(integration.id)} loading={isLoading} block style={{ background: integration.color, borderColor: integration.color }}>
            Connect Account
          </Button>
        </Space>
      </div>
    );
  };

  const selectedLocationName = locations.find(l => l.id === selectedLocation)?.name || 'Select a location';

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Integrations</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Connect advertising platforms per location. Up to {MAX_ACCOUNTS} accounts per platform per location.
        </Paragraph>
      </div>

      {/* Location Selector */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <EnvironmentOutlined style={{ fontSize: 20, color: '#3b82f6' }} />
          <div style={{ flex: 1 }}>
            <Text strong>Select Location</Text>
            <div style={{ marginTop: 8 }}>
              {locationsLoading ? (
                <Spin size="small" />
              ) : (
                <Select
                  value={selectedLocation}
                  onChange={setSelectedLocation}
                  style={{ width: '100%', maxWidth: 400 }}
                  size="large"
                  placeholder="Select a location..."
                >
                  {locations.map(loc => (
                    <Select.Option key={loc.id} value={loc.id}>
                      <EnvironmentOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                      {loc.name}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchLocations}>Refresh</Button>
        </div>
      </Card>

      {/* Integration Cards */}
      {selectedLocation ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Showing integrations for <Text strong>{selectedLocationName}</Text>
            </Text>
          </div>
          <Row gutter={[24, 24]}>
            {integrationsList.map((integration) => {
              const accounts = connections[integration.id] || [];
              return (
                <Col xs={24} lg={8} key={integration.id}>
                  <Card style={{ height: '100%', borderTop: `4px solid ${integration.color}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                      <div style={{ padding: '12px', background: `${integration.color}15`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {integration.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Title level={5} style={{ margin: 0 }}>{integration.name}</Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {accounts.length} of {MAX_ACCOUNTS} connected
                        </Text>
                      </div>
                    </div>
                    <Paragraph type="secondary" style={{ fontSize: '13px', marginBottom: '20px' }}>
                      {integration.description}
                    </Paragraph>
                    <div>
                      {[0, 1, 2].map(slotIndex => renderAccountSlot(integration, slotIndex))}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      ) : (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <EnvironmentOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
          <Title level={4} type="secondary">Select a Location</Title>
          <Paragraph type="secondary">Please select a location above to manage its integrations.</Paragraph>
        </Card>
      )}

      <Card style={{ marginTop: 24, background: '#f6f8fa' }}>
        <Title level={5}>Need Help?</Title>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Integrations allow you to connect advertising accounts per location and manage everything from one dashboard.
        </Paragraph>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Your credentials are securely stored and encrypted. You can disconnect any integration at any time.
        </Paragraph>
      </Card>
    </div>
  );
};

export default Integrations;
